// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  admin/src/pages/VendorOrders.jsx  — Quickry Admin
//  Copy to: C:\quickry\admin\src\pages\VendorOrders.jsx
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
import { useState, useEffect, useRef, useCallback } from "react";
import {
  UtensilsCrossed, ShoppingBasket, Bell, BellOff,
  Clock, CheckCircle2, XCircle, Bike, Loader2,
  Check, X, RefreshCw, Volume2, VolumeX, ChevronRight,
  Package, Phone, MapPin, Timer, IndianRupee, AlertCircle,
  Flame, Star,
} from "lucide-react";

const T = {
  primary:"#f97316", primaryDark:"#ea580c", primaryLight:"#fff7ed", primaryMid:"#fed7aa",
  white:"#ffffff", bg:"#f1f5f9", text:"#0f172a", textSub:"#64748b", textMuted:"#94a3b8",
  border:"#e2e8f0", green:"#16a34a", greenLight:"#dcfce7", red:"#ef4444", redLight:"#fef2f2",
  yellow:"#f59e0b", yellowLight:"#fefce8", blue:"#2563eb", blueLight:"#eff6ff",
  purple:"#7c3aed", purpleLight:"#f5f3ff",
};

const STATUS_FLOW = ["placed", "accepted", "preparing", "picked", "delivered"];
const STATUS_MAP = {
  placed:    { label:"New Order",   bg:T.blueLight,    color:T.blue,    icon:Clock,        next:"accepted"  },
  accepted:  { label:"Accepted",    bg:T.yellowLight,  color:T.yellow,  icon:CheckCircle2, next:"preparing" },
  preparing: { label:"Preparing",   bg:T.primaryLight, color:T.primary, icon:Loader2,      next:"picked"    },
  picked:    { label:"Picked Up",   bg:T.purpleLight,  color:T.purple,  icon:Bike,         next:"delivered" },
  delivered: { label:"Delivered",   bg:T.greenLight,   color:T.green,   icon:CheckCircle2, next:null        },
  cancelled: { label:"Cancelled",   bg:T.redLight,     color:T.red,     icon:XCircle,      next:null        },
};

const isProd = typeof window !== "undefined" && window.location.hostname !== "localhost";
const API_BASE = isProd ? "https://quickry-ivrv.onrender.com/api" : "http://localhost:5000/api";
const getToken = () => localStorage.getItem("adminToken") || "";

const req = async (method, url, body = null) => {
  try {
    const res = await fetch(`${API_BASE}${url}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    return await res.json();
  } catch (e) {
    return { success: false, message: e.message };
  }
};

// ── Alert Sound (Web Audio API — no file needed) ──
function playAlertSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const times = [0, 0.15, 0.3];
    times.forEach(t => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(880, ctx.currentTime + t);
      osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + t + 0.1);
      gain.gain.setValueAtTime(0.3, ctx.currentTime + t);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.12);
      osc.start(ctx.currentTime + t);
      osc.stop(ctx.currentTime + t + 0.15);
    });
  } catch(e) { /* silent fail */ }
}

// ── Countdown Timer component ─────────────────────
function OrderTimer({ createdAt }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = new Date(createdAt).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
    tick();
    const id = setInterval(tick, 10000);
    return () => clearInterval(id);
  }, [createdAt]);
  const mins = Math.floor(elapsed / 60);
  const isLate = mins >= 30;
  return (
    <span style={{ fontSize:"0.68rem", fontWeight:700, color:isLate?T.red:T.textMuted, display:"flex", alignItems:"center", gap:3 }}>
      <Timer size={11}/> {mins}m ago
    </span>
  );
}

// ── Order Card ────────────────────────────────────
function OrderCard({ order, serviceType, onStatusChange, highlight }) {
  const [updating, setUpdating] = useState(false);
  const s = STATUS_MAP[order.status] || STATUS_MAP.placed;
  const SIcon = s.icon;
  const nextStatus = s.next;
  const isNew = order.status === "placed";

  const handleProgress = async () => {
    if (!nextStatus) return;
    setUpdating(true);
    const res = await req("PATCH", `/orders/${order._id}/status`, { status: nextStatus });
    setUpdating(false);
    if (res.success) onStatusChange(order._id, nextStatus);
  };

  const handleCancel = async () => {
    if (!window.confirm("Order cancel karna hai?")) return;
    setUpdating(true);
    const res = await req("PATCH", `/orders/${order._id}/status`, {
      status: "cancelled",
      reason: "Admin cancelled",
      cancelledBy: "admin",
    });
    setUpdating(false);
    if (res.success) onStatusChange(order._id, "cancelled");
  };

  const nextLabel = nextStatus
    ? { accepted:"Accept", preparing:"Start Preparing", picked:"Mark Picked", delivered:"Mark Delivered" }[nextStatus]
    : null;

  return (
    <div style={{
      background: T.white,
      borderRadius: 18,
      border: `2px solid ${isNew && highlight ? T.primary : s.color + "33"}`,
      padding: "18px",
      animation: highlight ? "newOrderPulse 1s ease" : `fadeUp .3s ease both`,
      boxShadow: isNew && highlight ? `0 0 0 4px ${T.primaryMid}` : "0 2px 8px rgba(0,0,0,0.04)",
      position: "relative",
      overflow: "hidden",
    }}>
      {isNew && (
        <div style={{ position:"absolute", top:0, right:0, background:T.primary, color:"#fff", fontSize:"0.6rem", fontWeight:800, padding:"3px 10px", borderRadius:"0 16px 0 10px", letterSpacing:"1px" }}>
          NEW
        </div>
      )}

      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ width:36, height:36, borderRadius:10, background:s.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <SIcon size={17} color={s.color} style={order.status==="preparing"?{animation:"spin 1.5s linear infinite"}:{}}/>
          </div>
          <div>
            <div style={{ fontWeight:800, fontSize:"0.88rem", color:T.text }}>#{order.orderId?.slice(-6)||order._id?.slice(-6)}</div>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ background:s.bg, color:s.color, fontSize:"0.62rem", fontWeight:700, padding:"1px 7px", borderRadius:100 }}>{s.label}</span>
              <OrderTimer createdAt={order.createdAt}/>
            </div>
          </div>
        </div>
        <div style={{ textAlign:"right" }}>
          <div style={{ fontWeight:900, fontSize:"1.1rem", color:T.text, fontFamily:"'Fraunces',Georgia,serif" }}>₹{order.total}</div>
          <div style={{ fontSize:"0.65rem", color:T.textMuted, textTransform:"uppercase", fontWeight:600 }}>{order.paymentMode}</div>
        </div>
      </div>

      {/* Customer */}
      <div style={{ background:T.bg, borderRadius:10, padding:"10px 12px", marginBottom:10 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
          <div style={{ fontWeight:700, fontSize:"0.83rem", color:T.text }}>{order.user?.name||"Customer"}</div>
          {order.user?.phone && (
            <a href={`tel:${order.user.phone}`}
              style={{ display:"flex", alignItems:"center", gap:4, background:T.greenLight, color:T.green, fontSize:"0.7rem", fontWeight:700, padding:"3px 9px", borderRadius:100, textDecoration:"none" }}>
              <Phone size={10}/> Call
            </a>
          )}
        </div>
        {order.deliveryAddress && (
          <div style={{ display:"flex", alignItems:"flex-start", gap:5, fontSize:"0.72rem", color:T.textSub }}>
            <MapPin size={11} style={{ flexShrink:0, marginTop:1 }} color={T.textMuted}/>
            {order.deliveryAddress.line1}{order.deliveryAddress.city ? `, ${order.deliveryAddress.city}` : ""}
          </div>
        )}
      </div>

      {/* Items */}
      <div style={{ marginBottom:10 }}>
        {(order.items||[]).slice(0,3).map((item,i) => (
          <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"4px 0", borderBottom:i<Math.min(order.items.length,3)-1?`1px solid ${T.border}`:"none" }}>
            <span style={{ fontSize:"0.78rem", color:T.text }}>{item.qty}× {item.name}</span>
            <span style={{ fontSize:"0.78rem", fontWeight:700, color:T.textSub }}>₹{item.price*item.qty}</span>
          </div>
        ))}
        {order.items?.length > 3 && (
          <div style={{ fontSize:"0.7rem", color:T.textMuted, marginTop:3 }}>+{order.items.length-3} more items</div>
        )}
      </div>

      {/* Special instructions */}
      {order.specialInstructions && (
        <div style={{ background:T.yellowLight, border:`1px solid #fde68a`, borderRadius:8, padding:"7px 10px", marginBottom:10, fontSize:"0.72rem", color:"#92400e" }}>
          📝 {order.specialInstructions}
        </div>
      )}

      {/* Actions */}
      {order.status !== "delivered" && order.status !== "cancelled" && (
        <div style={{ display:"flex", gap:8, marginTop:4 }}>
          {nextLabel && (
            <button onClick={handleProgress} disabled={updating}
              style={{ flex:2, background:`linear-gradient(135deg,${T.primaryDark},${T.primary})`, border:"none", borderRadius:11, padding:"10px", color:"#fff", fontWeight:800, fontSize:"0.82rem", cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:6, boxShadow:"0 3px 10px rgba(249,115,22,0.3)" }}>
              {updating ? <Loader2 size={14} style={{ animation:"spin 1s linear infinite" }}/> : <Check size={14}/>}
              {nextLabel}
            </button>
          )}
          <button onClick={handleCancel} disabled={updating}
            style={{ flex:1, background:T.redLight, border:`1px solid #fecaca`, borderRadius:11, padding:"10px", color:T.red, fontWeight:700, fontSize:"0.78rem", cursor:"pointer", fontFamily:"inherit" }}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// ── Vendor Column ──────────────────────────────────
function VendorColumn({ vendor, orders, serviceType, onStatusChange, newOrderIds }) {
  const activeOrders = orders.filter(o => o.status !== "delivered" && o.status !== "cancelled");
  const totalRevenue = orders.filter(o=>o.status==="delivered").reduce((s,o)=>s+o.total,0);

  return (
    <div style={{ background:T.bg, borderRadius:20, padding:"16px", minWidth:320, maxWidth:360, flex:"1 1 320px" }}>
      {/* Vendor header */}
      <div style={{ background:T.white, borderRadius:14, padding:"14px 16px", marginBottom:14, border:`1px solid ${T.border}` }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:40, height:40, borderRadius:12, background:serviceType==="food"?`linear-gradient(135deg,${T.primaryDark},${T.primary})`:`linear-gradient(135deg,#16a34a,#22c55e)`, display:"flex", alignItems:"center", justifyContent:"center" }}>
              {serviceType==="food" ? <UtensilsCrossed size={18} color="#fff"/> : <ShoppingBasket size={18} color="#fff"/>}
            </div>
            <div>
              <div style={{ fontWeight:800, fontSize:"0.9rem", color:T.text, maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{vendor.name}</div>
              <div style={{ fontSize:"0.68rem", color:T.textMuted }}>{vendor.area||"Akola"}</div>
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background:vendor.isOpen!==false?T.green:T.red }}/>
            <span style={{ fontSize:"0.68rem", fontWeight:700, color:vendor.isOpen!==false?T.green:T.red }}>{vendor.isOpen!==false?"Open":"Closed"}</span>
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
          {[
            { label:"Active", val:activeOrders.length, color:T.primary },
            { label:"Total", val:orders.length, color:T.text },
            { label:"Revenue", val:`₹${totalRevenue}`, color:T.green },
          ].map((s,i) => (
            <div key={i} style={{ background:T.bg, borderRadius:8, padding:"7px", textAlign:"center" }}>
              <div style={{ fontWeight:800, fontSize:"0.85rem", color:s.color }}>{s.val}</div>
              <div style={{ fontSize:"0.58rem", color:T.textMuted, fontWeight:600 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Orders */}
      <div style={{ display:"flex", flexDirection:"column", gap:12, maxHeight:"calc(100vh - 280px)", overflowY:"auto", paddingRight:4 }}>
        {orders.length === 0 ? (
          <div style={{ textAlign:"center", padding:"32px 16px", color:T.textMuted }}>
            <Package size={32} color={T.border} style={{ marginBottom:8 }}/>
            <div style={{ fontSize:"0.78rem", fontWeight:600 }}>Koi orders nahi abhi</div>
          </div>
        ) : (
          orders.map(order => (
            <OrderCard
              key={order._id}
              order={order}
              serviceType={serviceType}
              onStatusChange={onStatusChange}
              highlight={newOrderIds.has(order._id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ── MAIN PAGE ──────────────────────────────────────
export default function VendorOrders() {
  const [serviceTab,  setServiceTab]  = useState("food"); // food | grocery
  const [vendors,     setVendors]     = useState([]);
  const [allOrders,   setAllOrders]   = useState({});     // vendorId → orders[]
  const [loading,     setLoading]     = useState(true);
  const [soundOn,     setSoundOn]     = useState(true);
  const [toast,       setToast]       = useState("");
  const [newOrderIds, setNewOrderIds] = useState(new Set());
  const [filterStatus,setFilterStatus]= useState("active"); // active | all
  const [lastCount,   setLastCount]   = useState(0);
  const pollRef = useRef(null);
  const mountedRef = useRef(true);

  const showToast = m => { setToast(m); setTimeout(()=>setToast(""),2200); };

  // Load vendors + their orders
  const loadData = useCallback(async (isPolling = false) => {
    if (!mountedRef.current) return;
    if (!isPolling) setLoading(true);

    try {
      // Fetch vendors by service type
      const vendorType = serviceTab === "food" ? "Restaurant" : "Grocery";
      const [vendorRes, orderRes] = await Promise.all([
        req("GET", `/vendors?type=${vendorType}`),
        req("GET", `/orders/vendor-dashboard?type=${serviceTab}&status=${filterStatus === "active" ? "placed,accepted,preparing,picked" : "all"}`),
      ]);

      if (!mountedRef.current) return;

      if (vendorRes.success) setVendors(vendorRes.data || []);

      if (orderRes.success) {
        const orders = orderRes.data || [];

        // Group orders by vendor
        const grouped = {};
        orders.forEach(order => {
          const vid = order.vendorId || order.restaurant?._id || order.store?._id;
          if (vid) {
            if (!grouped[vid]) grouped[vid] = [];
            grouped[vid].push(order);
          }
        });

        // Detect new orders (polling only)
        if (isPolling) {
          const currentTotal = orders.filter(o=>o.status==="placed").length;
          if (currentTotal > lastCount) {
            const newCount = currentTotal - lastCount;
            const newIds = new Set(
              orders.filter(o=>o.status==="placed").slice(0,newCount).map(o=>o._id)
            );
            setNewOrderIds(newIds);
            if (soundOn) playAlertSound();
            showToast(`🔔 ${newCount} naya order aaya!`);
            // Clear highlight after 5s
            setTimeout(() => setNewOrderIds(new Set()), 5000);
          }
          setLastCount(currentTotal);
        } else {
          setLastCount(orders.filter(o=>o.status==="placed").length);
        }

        setAllOrders(grouped);
      }
    } catch (e) {
      if (!isPolling) showToast("Backend connect nahi hua");
    } finally {
      if (!isPolling && mountedRef.current) setLoading(false);
    }
  }, [serviceTab, filterStatus, soundOn, lastCount]);

  // Initial load
  useEffect(() => {
    mountedRef.current = true;
    loadData(false);
    return () => { mountedRef.current = false; };
  }, [serviceTab, filterStatus]);

  // Polling every 15 seconds
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => loadData(true), 15000);
    return () => clearInterval(pollRef.current);
  }, [loadData]);

  const handleStatusChange = (orderId, newStatus) => {
    setAllOrders(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(vid => {
        updated[vid] = updated[vid].map(o => o._id===orderId ? {...o,status:newStatus} : o);
      });
      return updated;
    });
    showToast(`Order ${newStatus}!`);
  };

  // Total stats
  const allOrdersFlat = Object.values(allOrders).flat();
  const totalActive   = allOrdersFlat.filter(o=>o.status==="placed"||o.status==="accepted"||o.status==="preparing").length;
  const totalPreparing = allOrdersFlat.filter(o=>o.status==="preparing").length;
  const totalPicked   = allOrdersFlat.filter(o=>o.status==="picked").length;

  return (
    <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <style>{`
        @keyframes fadeUp    { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin      { to { transform: rotate(360deg); } }
        @keyframes newOrderPulse {
          0%   { box-shadow: 0 0 0 0 rgba(249,115,22,0.6); }
          50%  { box-shadow: 0 0 0 12px rgba(249,115,22,0); }
          100% { box-shadow: 0 0 0 0 rgba(249,115,22,0); }
        }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 4px; }
      `}</style>

      {/* Toast */}
      {toast && (
        <div style={{ position:"fixed", top:80, left:"50%", transform:"translateX(-50%)", background:T.text, color:"#fff", padding:"10px 20px", borderRadius:100, fontSize:"0.82rem", fontWeight:600, zIndex:600, display:"flex", alignItems:"center", gap:7, whiteSpace:"nowrap", animation:"fadeUp .2s ease" }}>
          <Bell size={13} color={T.primary}/> {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.2rem", color:T.text }}>
            Vendor Orders
          </div>
          <div style={{ fontSize:"0.72rem", color:T.textMuted, marginTop:2 }}>
            Live orders — auto-refresh har 15 seconds
          </div>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          {/* Sound toggle */}
          <button onClick={() => setSoundOn(s=>!s)}
            style={{ background:soundOn?T.primaryLight:T.bg, border:`1.5px solid ${soundOn?T.primary:T.border}`, borderRadius:10, padding:"8px 14px", cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontSize:"0.78rem", fontWeight:700, color:soundOn?T.primary:T.textSub, fontFamily:"inherit" }}>
            {soundOn ? <Volume2 size={14}/> : <VolumeX size={14}/>}
            Alert Sound {soundOn?"ON":"OFF"}
          </button>

          {/* Filter */}
          <div style={{ display:"flex", background:T.white, border:`1px solid ${T.border}`, borderRadius:10, padding:3, gap:2 }}>
            {[["active","Active"], ["all","All"]].map(([val,label]) => (
              <button key={val} onClick={()=>setFilterStatus(val)}
                style={{ background:filterStatus===val?T.primary:"none", color:filterStatus===val?"#fff":T.textSub, border:"none", borderRadius:8, padding:"6px 14px", fontWeight:600, fontSize:"0.78rem", cursor:"pointer", fontFamily:"inherit", transition:"all .2s" }}>
                {label}
              </button>
            ))}
          </div>

          <button onClick={()=>loadData(false)}
            style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:10, padding:"8px 14px", cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontSize:"0.78rem", fontWeight:600, color:T.textSub, fontFamily:"inherit" }}>
            <RefreshCw size={14} style={loading?{animation:"spin 1s linear infinite"}:{}}/> Refresh
          </button>
        </div>
      </div>

      {/* Service tabs */}
      <div style={{ display:"flex", gap:10, marginBottom:18 }}>
        {[
          { id:"food",    label:"Food / Restaurant", Icon:UtensilsCrossed, color:T.primary, bg:T.primaryLight },
          { id:"grocery", label:"Grocery",           Icon:ShoppingBasket,  color:T.green,   bg:T.greenLight   },
        ].map(tab => (
          <button key={tab.id} onClick={()=>setServiceTab(tab.id)}
            style={{ display:"flex", alignItems:"center", gap:9, padding:"11px 20px", borderRadius:14, border:`2px solid ${serviceTab===tab.id?tab.color:T.border}`, background:serviceTab===tab.id?tab.bg:"#fff", cursor:"pointer", fontFamily:"inherit", transition:"all .2s" }}>
            <tab.Icon size={17} color={serviceTab===tab.id?tab.color:T.textSub}/>
            <span style={{ fontWeight:700, fontSize:"0.85rem", color:serviceTab===tab.id?tab.color:T.textSub }}>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Stats bar */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:10, marginBottom:18 }}>
        {[
          { label:"Pending",    val:allOrdersFlat.filter(o=>o.status==="placed").length,    color:T.blue,    bg:T.blueLight    },
          { label:"Accepted",   val:allOrdersFlat.filter(o=>o.status==="accepted").length,  color:T.yellow,  bg:T.yellowLight  },
          { label:"Preparing",  val:totalPreparing,                                          color:T.primary, bg:T.primaryLight },
          { label:"On the way", val:totalPicked,                                             color:T.purple,  bg:T.purpleLight  },
        ].map((s,i) => (
          <div key={i} style={{ background:s.bg, borderRadius:12, padding:"13px 14px", border:`1px solid ${T.border}` }}>
            <div style={{ fontWeight:900, fontSize:"1.5rem", color:s.color, fontFamily:"'Fraunces',Georgia,serif" }}>{loading?"—":s.val}</div>
            <div style={{ fontSize:"0.7rem", color:T.textSub, fontWeight:600, marginTop:2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Vendor columns */}
      {loading ? (
        <div style={{ textAlign:"center", padding:60, color:T.textMuted }}>
          <Loader2 size={28} style={{ animation:"spin 1s linear infinite", color:T.primary, marginBottom:10 }}/>
          <div>Orders load ho rahe hain...</div>
        </div>
      ) : vendors.length === 0 ? (
        <div style={{ textAlign:"center", padding:60, background:T.white, borderRadius:16, border:`1px solid ${T.border}` }}>
          <UtensilsCrossed size={44} color={T.border} style={{ marginBottom:12 }}/>
          <div style={{ fontWeight:700, color:T.textSub, marginBottom:6 }}>
            Koi {serviceTab === "food" ? "restaurants" : "grocery stores"} nahi
          </div>
          <div style={{ fontSize:"0.78rem", color:T.textMuted }}>
            Pehle {serviceTab === "food" ? "Restaurants" : "Grocery Stores"} page pe vendor add karo
          </div>
        </div>
      ) : (
        <div style={{ display:"flex", gap:16, overflowX:"auto", paddingBottom:16, alignItems:"flex-start" }}>
          {vendors.map(vendor => {
            const vendOrders = allOrders[vendor._id] || [];
            const displayOrders = filterStatus === "active"
              ? vendOrders.filter(o => ["placed","accepted","preparing","picked"].includes(o.status))
              : vendOrders;
            return (
              <VendorColumn
                key={vendor._id}
                vendor={vendor}
                orders={displayOrders}
                serviceType={serviceTab}
                onStatusChange={handleStatusChange}
                newOrderIds={newOrderIds}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}